import { act, fireEvent, screen } from '@testing-library/react'
import { vi } from 'vitest'

export const imageProbes: ImageProbe[] = []
export const resizeObservers: ResizeObserverProbe[] = []

export class PointerEventProbe extends MouseEvent {
  readonly pointerId: number

  constructor(type: string, init: PointerEventInit = {}) {
    super(type, init)
    this.pointerId = init.pointerId ?? 0
  }
}

export class ImageProbe {
  src = ''
  naturalWidth = 0
  naturalHeight = 0
  onload: ((event: Event) => void) | null = null
  onerror: ((event: Event) => void) | null = null

  constructor() {
    imageProbes.push(this)
  }

  load(width: number, height: number) {
    this.naturalWidth = width
    this.naturalHeight = height
    this.onload?.(new Event('load'))
  }

  fail() {
    this.onerror?.(new Event('error'))
  }
}

export class ResizeObserverProbe implements ResizeObserver {
  observed?: Element

  constructor(readonly callback: ResizeObserverCallback) {
    resizeObservers.push(this)
  }

  observe(target: Element) {
    this.observed = target
  }
  unobserve() {}
  disconnect() {}
  takeRecords() {
    return []
  }
  notify() {
    this.callback([], this)
  }
  resize(width: number, height: number) {
    if (!this.observed) return
    this.callback(
      [
        {
          target: this.observed,
          contentRect: new DOMRect(0, 0, width, height),
        } as ResizeObserverEntry,
      ],
      this
    )
  }
}

export const resetMarkupProbes = () => {
  imageProbes.splice(0)
  resizeObservers.splice(0)
}

export const stubMarkupProbeGlobals = () => {
  vi.stubGlobal('PointerEvent', PointerEventProbe)
  vi.stubGlobal('ResizeObserver', ResizeObserverProbe)
  vi.stubGlobal('Image', ImageProbe)
}

export const loadMarkupSurface = (
  imageWidth: number,
  imageHeight: number,
  surfaceWidth: number,
  surfaceHeight: number
) => {
  const viewport = screen.getByLabelText('Markup page viewport')
  Object.defineProperty(viewport, 'clientWidth', {
    configurable: true,
    value: surfaceWidth,
  })
  const observer = resizeObservers.findLast(
    (item) => item.observed === viewport
  )
  if (!observer)
    throw new Error('Expected a resize observer for the markup viewport')
  act(() => observer.notify())

  const image = screen.getByAltText<HTMLImageElement>('Markup image surface')
  Object.defineProperties(image, {
    naturalWidth: { configurable: true, value: imageWidth },
    naturalHeight: { configurable: true, value: imageHeight },
  })
  fireEvent.load(image)

  const surface = screen.getByRole('application')
  for (const element of [
    document.querySelector<HTMLElement>('[data-markup-page-frame]'),
    document.querySelector<HTMLElement>(
      '[aria-label="Document markup annotations"]'
    ),
    surface,
  ]) {
    if (!element) continue
    Object.defineProperty(element, 'getBoundingClientRect', {
      configurable: true,
      value: () => new DOMRect(10, 20, surfaceWidth, surfaceHeight),
    })
  }
  Object.defineProperties(surface, {
    releasePointerCapture: { configurable: true, value: vi.fn() },
    setPointerCapture: { configurable: true, value: vi.fn() },
  })
  return surface
}

export const resizeMarkupViewport = (
  surface: Element,
  width: number,
  height: number
) => {
  const viewport = screen.getByLabelText('Markup page viewport')
  Object.defineProperty(viewport, 'clientWidth', {
    configurable: true,
    value: width,
  })
  const observer = resizeObservers.findLast(
    (item) => item.observed === viewport
  )
  if (!observer)
    throw new Error('Expected a resize observer for the markup viewport')
  act(() => observer.notify())
  for (const element of [
    document.querySelector<HTMLElement>('[data-markup-page-frame]'),
    document.querySelector<HTMLElement>(
      '[aria-label="Document markup annotations"]'
    ),
    surface,
  ]) {
    if (!element) continue
    Object.defineProperty(element, 'getBoundingClientRect', {
      configurable: true,
      value: () => new DOMRect(10, 20, width, height),
    })
  }
}

export const pointer = (
  surface: Element,
  type: 'pointerDown' | 'pointerMove' | 'pointerUp',
  x: number,
  y: number
) => fireEvent[type](surface, { clientX: x, clientY: y, pointerId: 1 })

export const draw = (
  surface: Element,
  start: { x: number; y: number },
  end: { x: number; y: number }
) => {
  pointer(surface, 'pointerDown', start.x, start.y)
  pointer(surface, 'pointerMove', end.x, end.y)
  pointer(surface, 'pointerUp', end.x, end.y)
}

export const startAnnotationDrag = (
  target: Element,
  start: { x: number; y: number },
  end: { x: number; y: number }
) => {
  fireEvent.mouseDown(target, {
    button: 0,
    buttons: 1,
    clientX: start.x,
    clientY: start.y,
  })
  fireEvent.mouseMove(document, { buttons: 1, clientX: end.x, clientY: end.y })
}

export const finishAnnotationDrag = (end: { x: number; y: number }) =>
  fireEvent.mouseUp(document, { button: 0, clientX: end.x, clientY: end.y })
